/**
 * rondo-web-forms
 *
 * Rondo Web Forms
 *
 * @author Jiri Hybek <jiri@hybek.cz>
 * @license See LICENSE file distributed with this source code.
 */

import { FormElement } from "../FormElement";
import { IFormElementConfig } from "../IFormElementConfig";
import { IFormElementFactory } from "../IFormElementFactory";
import { FormModel } from "../FormModel";
import { FORM_EL_TYPE } from "../FormElementTypes";

/**
 * Element config
 */
export interface IFormSectionConfig extends IFormElementConfig {
	/** Section width */
	width?: "1"|"1/2"|"1/3"|"2/3";

	/** Section contents */
	content?: Array<IFormElementFactory<any>>;
}

/**
 * Section
 */
export class FormSection extends FormElement<IFormSectionConfig> {

	/** Content elements */
	protected content: Array<FormElement<any>> = [];

	/**
	 * Element constructor
	 *
	 * @param model Model instance
	 * @param parent Parent element
	 * @param config Element configuration
	 */
	public constructor(
		protected model: FormModel,
		protected parent: FormElement<any>,
		protected config: IFormSectionConfig
	) {

		super(model, parent, FORM_EL_TYPE.SECTION, config);

		// Init content components
		if (config.content) {

			for (let i = 0; i < config.content.length; i++) {
				this.content.push(
					config.content[i](this.model, this)
				);
			}

		}

	}

	/**
	 * Updates the element
	 *
	 * @param resetState If to reset state
	 */
	public update(resetState: boolean = false) {

		// Call update on all content elements
		for (let i = 0; i < this.content.length; i++) {
			this.content[i].update(resetState);
		}

	}

	/**
	 * Returns element state
	 */
	public getState() {

		return {
			...super.getState(),
			content: this.content
		};

	}

	/**
	 * Destroys the element
	 */
	public destroy() {

		// Call destroy on all content elements
		for (let i = 0; i < this.content.length; i++) {
			this.content[i].destroy();
		}

		super.destroy();

	}

}

/**
 * Declares section group
 *
 * @param config Config
 * @param content Content elements
 */
export function Section(
	config: IFormSectionConfig,
	content: Array<IFormElementFactory<any>> = []
) {

	const _config = {
		content: content,
		...config
	};

	return (model, parent) => new FormSection(model, parent, _config);

}
